import { useQuery } from '@tanstack/react-query';
import { lotusHealthZod } from '@lotushealth/lotushealth_js_sdk';
import { fetchCurrentClinicianOnboardingStep } from '@lotushealth/lotushealth_js_sdk/endpoints';
import type {
  AnswerQuestionsDataT,
  AwaitingDosespotDataT,
  EnterPrescriberDetailsDataT,
  FetchCurrentClinicianOnboardingStepReplyT,
  FillEmployeeRecordDataT,
  SignLegalAgreementsDataT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { createLotusRequestConfig } from '@/shared/api/lotus';

import { clinicianOnboardingKeys } from './keys';

const POLL_INTERVAL_MS = 5_000;

export type CurrentClinicianOnboardingServerStep =
  | { readonly kind: 'submit_application' }
  | { readonly kind: 'application_pending' }
  | { readonly kind: 'application_rejected' }
  | { readonly kind: 'fill_credentialing_form' }
  | { readonly kind: 'credentialing_pending' }
  | { readonly kind: 'fill_employee_record'; readonly data: FillEmployeeRecordDataT }
  | { readonly kind: 'enter_prescriber_details'; readonly data: EnterPrescriberDetailsDataT }
  | { readonly kind: 'awaiting_dosespot'; readonly data: AwaitingDosespotDataT }
  | { readonly kind: 'start_idp' }
  | { readonly kind: 'sign_disclaimer' }
  | { readonly kind: 'sign_legal_agreements'; readonly data: SignLegalAgreementsDataT }
  | { readonly kind: 'submit_demographics' }
  | { readonly kind: 'answer_questions'; readonly data: AnswerQuestionsDataT }
  | { readonly kind: 'submit_otp' }
  | { readonly kind: 'link_bank_account' }
  | { readonly kind: 'complete' };

function requireData<T>(
  response: FetchCurrentClinicianOnboardingStepReplyT,
  stepKind: CurrentClinicianOnboardingServerStep['kind']
): T {
  if (response.data === undefined || response.data === null) {
    throw new Error(`Clinician onboarding step ${stepKind} is missing required data.`);
  }
  return response.data as T;
}

function toCurrentStep(
  response: FetchCurrentClinicianOnboardingStepReplyT
): CurrentClinicianOnboardingServerStep {
  switch (response.step) {
    case 'submit_application':
      return { kind: 'submit_application' };

    case 'application_pending':
      return { kind: 'application_pending' };

    case 'application_rejected':
      return { kind: 'application_rejected' };

    case 'fill_credentialing_form':
      return { kind: 'fill_credentialing_form' };

    case 'credentialing_pending':
      return { kind: 'credentialing_pending' };

    case 'fill_employee_record':
      return {
        kind: 'fill_employee_record',
        data: requireData<FillEmployeeRecordDataT>(response, 'fill_employee_record'),
      };

    case 'enter_prescriber_details':
      return {
        kind: 'enter_prescriber_details',
        data: requireData<EnterPrescriberDetailsDataT>(response, 'enter_prescriber_details'),
      };

    case 'awaiting_dosespot':
      return {
        kind: 'awaiting_dosespot',
        data: requireData<AwaitingDosespotDataT>(response, 'awaiting_dosespot'),
      };

    case 'sign_legal_agreements':
      return {
        kind: 'sign_legal_agreements',
        data: requireData<SignLegalAgreementsDataT>(response, 'sign_legal_agreements'),
      };

    case 'answer_questions':
      return {
        kind: 'answer_questions',
        data: requireData<AnswerQuestionsDataT>(response, 'answer_questions'),
      };

    case 'start_idp':
      return { kind: 'start_idp' };

    case 'sign_disclaimer':
      return { kind: 'sign_disclaimer' };

    case 'submit_demographics':
      return { kind: 'submit_demographics' };

    case 'submit_otp':
      return { kind: 'submit_otp' };

    case 'link_bank_account':
      return { kind: 'link_bank_account' };

    case 'complete':
      return { kind: 'complete' };

    default: {
      const _exhaustive: never = response.step;
      void _exhaustive;
      throw new Error('Received unsupported clinician onboarding step.');
    }
  }
}

export function currentClinicianOnboardingStepQueryDefinition() {
  return {
    queryKey: clinicianOnboardingKeys.currentStep(),
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const config = await createLotusRequestConfig({ signal });
      const response = await fetchCurrentClinicianOnboardingStep(config);
      const parsed = lotusHealthZod.fetchCurrentClinicianOnboardingStepResponse.parse(
        response.data
      ) as FetchCurrentClinicianOnboardingStepReplyT;
      return toCurrentStep(parsed);
    },
    meta: {
      toast: {
        title: 'Could not load onboarding status',
        description: 'Your clinician onboarding step could not be fetched.',
      },
    },
    refetchInterval: (query: { state: { data?: CurrentClinicianOnboardingServerStep } }) => {
      const step = query.state.data?.kind;
      switch (step) {
        case 'application_pending':
        case 'credentialing_pending':
        case 'awaiting_dosespot':
        case 'start_idp':
        case 'answer_questions':
        case 'submit_otp':
          return POLL_INTERVAL_MS;
        default:
          return false;
      }
    },
    refetchIntervalInBackground: false,
  } as const;
}

export function useCurrentStepQuery(enabled = true) {
  return useQuery({
    ...currentClinicianOnboardingStepQueryDefinition(),
    enabled,
  });
}
